import Input from "@/components/common/Input";
import {
    MDBBtn,
    MDBCheckbox,
    MDBCol,
    MDBRadio,
    MDBRow,
    MDBTextArea,
} from "mdb-react-ui-kit";
import ReactFlagsSelect from "react-flags-select";
import { PhoneInput } from "react-international-phone";
import styles from "@/styles/common/RequestAQuoteModal.module.scss";
import React, { useCallback, useState, FC, useEffect, useRef } from "react";
import { emptyRequestAQuote, IRequestAQuote } from "@/models/request-a-quote";
import moment from "moment";
import { ActivityReableTO } from "@/models/common/common";
import { chekStartOrEndWithSpace, isValidEmail } from "@/utils/common";
import "react-international-phone/style.css";
import { requestAQuoteAPI } from "@/utils/queries/checkout-queries";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useStore } from "@/store/index";
import { ETypeToastMessage } from "@/models/common/enums";
import { useIsSM } from "@/utils/hooks/useWindowSizes";
import CitiesGoogle from "@/components/CitiesGoogle";
import { Chip } from "@/components/search/Chip";
import { getRequestAQuoteFormService } from "@/utils/queries";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { convertDeltaToHtmlConverter } from "@/utils/common/delta";
import { IUTMParams } from "@/models/store/user";
import TagManager from "react-gtm-module";
import {
    startRequestQuote,
    requestQuote,
    GA4Item,
} from "@/utils/analytics/tagmanager";
import { randomUid } from "@/utils/randomIds";

import RangeDatePicker from "../addToCart/datepicker/RangeDatePicker";

export const RequestAQuoteForm: FC<{
    isInModal?: boolean;
    fromSmallDevices?: boolean;
    id: string;
    title: string;
    activities: ActivityReableTO[];
    setShow?: () => void;
    // eslint-disable-next-line sonarjs/cognitive-complexity
}> = ({ setShow, id, title, fromSmallDevices, activities = [], isInModal }) => {
    // STATE
    const [formValue, setFormValue] = useState(emptyRequestAQuote(id));
    const [privacyPolicy, setPrivacyPolicy] = useState<boolean>(true);
    const [rangeLength, setRangeLength] = useState(1);
    const [click, setClick] = useState(false);
    const [isPhoneValid, setIsPhoneValid] = useState(false);

    const eventFired = useRef(false);

    const countrySelectStyles = "2px solid #e6e8ec";

    // STORE
    const dispatch = useStore(state => state.dispatchCommon);
    const setErrorMsg = useStore(state => state.setErrorMsg);

    // QUERY
    const { data: requestAQuoteFormMetadata } = useQuery({
        queryKey: ["requestAQuoteForm"],
        queryFn: getRequestAQuoteFormService,
        refetchInterval: false,
        refetchOnWindowFocus: false,
    });

    // MUTATION
    const { mutate: requestAQuoteMutation, isLoading } = useMutation(
        requestAQuoteAPI,
        {
            onSettled: () =>
                dispatch({ type: "SET_LOADER_PAGE", payload: false }),
            onSuccess: () => {
                dispatch({
                    type: "SET_TOAST_MESSAGE_DATA",
                    payload: {
                        type: ETypeToastMessage.SUCCESS,
                        message: `Request a quote sent!`,
                    },
                });
                if (setShow) setShow();
            },
            onError: (err: Error) => {
                setErrorMsg(err as never);
            },
        }
    );

    const handleFormInteraction = () => {
        if (!eventFired.current) {
            const item: GA4Item = {
                item_id: id,
                item_name: title,
            };
            const eventData = startRequestQuote(item);
            TagManager.dataLayer(eventData);
            eventFired.current = true;
        }
    };

    // UTILS
    const isSM = useIsSM();

    const onChange = (
        event: React.ChangeEvent<
            HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
    ) => {
        const { name, value } = event.target as never;
        let updateFormValue = false;

        switch (name) {
            case "age": {
                updateFormValue = value < 100;
                break;
            }
            case "numberPeopleComment":
            case "numberRoomsComment": {
                updateFormValue = value <= 999;
                break;
            }
            case "budgetPerPerson": {
                updateFormValue = value <= 99999;
                break;
            }
            default: {
                updateFormValue = true;
            }
        }
        if (updateFormValue) {
            setFormValue((prevState: IRequestAQuote) => ({
                ...prevState,
                [name]: value,
            }));
        }
    };

    const onDelete = (field: string) => {
        setFormValue((prevState: IRequestAQuote) => ({
            ...prevState,
            [field]: "",
        }));
    };

    const onChangeAnswer = (indexQuestion: number, value: string) => {
        // lo stesso indice della domanda è utilizzato anche nella risposta
        const { answers } = formValue;
        const answerToUpdate = answers.at(indexQuestion);
        if (answerToUpdate) {
            answers[indexQuestion] = {
                ...answerToUpdate,
                answer: value,
            };
        } else {
            answers.push({
                question:
                    requestAQuoteFormMetadata?.questions.at(indexQuestion) ??
                    "",
                answer: value,
            });
        }
        setFormValue((prevState: IRequestAQuote) => ({
            ...prevState,
            answers,
        }));
    };

    const getUTMParamsFromSession = (): IUTMParams => ({
        utmSource: sessionStorage.getItem("utm_source") || "",
        utmMedium: sessionStorage.getItem("utm_medium") || "",
        utmCampaign: sessionStorage.getItem("utm_campaign") || "",
        utmTerm: sessionStorage.getItem("utm_term") || "",
        utmContent: sessionStorage.getItem("utm_content") || "",
    });

    const generateLastDate = useCallback(() => {
        const date = new Date();
        date.setFullYear(date.getFullYear() + 1);
        return date;
    }, []);

    const formatRequestAQuoteActivities = useCallback(
        (requestAQuoteActivities: ActivityReableTO[]) => {
            return (requestAQuoteActivities ?? []).map(el => ({
                label: `${el.name}`,
                // eslint-disable-next-line no-underscore-dangle
                value: `${el._id}`,
            }));
        },
        [activities]
    );

    const selectOptions = useCallback(
        () =>
            ["Select...", "1", "2", "3", "4", "5", "6", "Other"].map(el => ({
                value: `${el === "Select..." ? "" : el}`,
                label: `${el}`,
            })),
        []
    );

    const onSelectDate = (dateSelected: Date) => {
        let prevStartDate = formValue.datesOfTravel.startDate;
        if (!prevStartDate) {
            prevStartDate = dateSelected;
        } else if (moment(dateSelected).isSame(prevStartDate)) {
            prevStartDate = null;
        }
        setRangeLength(
            prevStartDate
                ? moment(dateSelected).diff(prevStartDate, "days") + 1
                : 1
        );
        setFormValue((prevState: IRequestAQuote) => {
            return {
                ...prevState,
                datesOfTravel: {
                    startDate: prevStartDate,
                    endDate: dateSelected,
                },
            };
        });
    };

    /* eslint-disable  @typescript-eslint/no-explicit-any */
    const isFieldValid = (name: string, value: any, valueToReference?: any) => {
        switch (name) {
            case "activitiesToFocus":
            case "preferredDestinations":
            case "nationality": {
                return !!value;
            }
            case "alreadyBookAFlight":
            case "needTravelInsurance": {
                return value !== "NOT SELECTED";
            }
            case "firstName": {
                return chekStartOrEndWithSpace(value as string);
            }
            case "email": {
                return isValidEmail(value);
            }
            case "age": {
                return value >= 18 && value < 100;
            }
            case "budgetPerPerson": {
                return (
                    !(value ?? 0).toString().startsWith("0") &&
                    value > 0 &&
                    value <= 99999
                );
            }
            case "cellphone": {
                return !!value && isPossiblePhoneNumber(value);
            }
            case "numberPeople":
            case "currency": {
                return !!value;
            }
            case "numberRooms": {
                return (
                    !!value && Number(value) <= (Number(valueToReference) ?? 0)
                );
            }
            default: {
                return false;
            }
        }
    };

    const isFormValid = () => {
        return (
            privacyPolicy &&
            isFieldValid("nationality", formValue.nationality) &&
            isFieldValid("cellphone", formValue.cellphone) &&
            isFieldValid("numberPeople", formValue.numberPeople) &&
            isFieldValid("currency", formValue.currency) &&
            isFieldValid(
                "activitiesToFocus",
                formValue.activitiesToFocus.length
            ) &&
            isFieldValid(
                "preferredDestinations",
                formValue.preferredDestinations.length
            ) &&
            isFieldValid("alreadyBookAFlight", formValue.alreadyBookAFlight) &&
            isFieldValid(
                "needTravelInsurance",
                formValue.needTravelInsurance
            ) &&
            isFieldValid("firstName", formValue.firstName) &&
            isFieldValid("email", formValue.email) &&
            isFieldValid("age", formValue.age ?? 0) &&
            isFieldValid("budgetPerPerson", formValue.budgetPerPerson ?? 0) &&
            isFieldValid(
                "numberRooms",
                formValue.numberRooms === "Other"
                    ? formValue.numberRoomsComment
                    : formValue.numberRooms,
                formValue.numberPeople === "Other"
                    ? formValue.numberPeopleComment
                    : formValue.numberPeople
            )
        );
    };

    // USE EFFECT
    useEffect(() => {
        setFormValue(emptyRequestAQuote(id));
    }, [id]);

    useEffect(() => {
        dispatch({ type: "SET_LOADER_PAGE", payload: isLoading });
    }, [isLoading]);

    return (
        <MDBRow
            around
            className={styles.placeholderStyles}
            style={
                isInModal
                    ? {}
                    : {
                          border: "1px solid #a4cde3",
                          borderRadius: "24px",
                          padding: "20px",
                          backgroundColor: fromSmallDevices ? "white" : "",
                      }
            }
        >
            <MDBRow>
                <MDBCol size={isSM ? 7 : 9}>
                    <Input
                        columnProps={{ size: 12, className: "my-2" }}
                        label={requestAQuoteFormMetadata?.firstAndLastNameLabel}
                        required
                        type="text"
                        value={formValue?.firstName}
                        onChange={onChange}
                        onFocus={handleFormInteraction}
                        onDelete={() => onDelete("firstName")}
                        name="firstName"
                        validationCb={val => isFieldValid("firstName", val)}
                    />
                </MDBCol>
            </MDBRow>
            <MDBRow>
                <Input
                    columnProps={{ size: 6, className: "my-2" }}
                    label={requestAQuoteFormMetadata?.ageLabel}
                    inputClassName={`${styles.customNumberInput}`}
                    required
                    type="number"
                    value={formValue?.age}
                    onChange={onChange}
                    onFocus={handleFormInteraction}
                    name="age"
                    onDelete={() => onDelete("age")}
                    validationCb={val => isFieldValid("age", val)}
                />
                <div
                    className="my-2"
                    style={{
                        width: "50%",
                        position: "relative",
                    }}
                >
                    <p className={`${styles.paragraph}`}>
                        {requestAQuoteFormMetadata?.nationalityLabel}*
                    </p>
                    <ReactFlagsSelect
                        className={
                            formValue.nationality
                                ? styles.nationalityWithBorder
                                : ""
                        }
                        selected={formValue.nationality}
                        onSelect={code => {
                            setFormValue((prevState: IRequestAQuote) => ({
                                ...prevState,
                                nationality: code,
                            }));
                        }}
                    />
                </div>
                <Input
                    columnProps={{ size: 6, className: "my-2" }}
                    label={requestAQuoteFormMetadata?.emailLabel}
                    required
                    type="email"
                    value={formValue.email}
                    onChange={onChange}
                    name="email"
                    onDelete={() => onDelete("email")}
                    validationCb={val => isFieldValid("email", val)}
                />
                <MDBCol size={6} className="my-2">
                    <p
                        style={{
                            fontSize: "12px",
                            fontWeight: "400",
                            color: "rgb(129, 126, 145)",
                            marginBottom: "0.5rem",
                            fontFamily: "GeneralSans-Regular",
                        }}
                    >
                        {requestAQuoteFormMetadata?.phoneNumberLabel}*
                    </p>
                    <div style={{ position: "relative" }}>
                        <PhoneInput
                            value={formValue.cellphone}
                            onChange={phone => {
                                setFormValue((prevState: IRequestAQuote) => ({
                                    ...prevState,
                                    cellphone: phone,
                                }));
                                setIsPhoneValid(isPossiblePhoneNumber(phone)); // Aggiorna lo stato di validità
                            }}
                            style={{
                                width: "100%",
                                border: "unset",
                                borderColor: isPhoneValid
                                    ? "#55ce97"
                                    : "#e6e8ec",
                                borderRadius: "12px 12px 12px 12px",
                            }}
                            inputStyle={{
                                height: "45px",
                                borderRadius: "0px 12px 12px 0px",
                                border: countrySelectStyles,
                                borderColor: isPhoneValid
                                    ? "#55ce97"
                                    : "#e6e8ec",
                                borderLeft: "none",
                                outline: "none",
                                width: "100%",
                            }}
                            countrySelectorStyleProps={{
                                style: {
                                    height: "45px",
                                    borderRadius: "12px 0 0 12px",
                                    borderTop: countrySelectStyles,
                                    borderBottom: countrySelectStyles,
                                    borderLeft: countrySelectStyles,
                                    borderColor: isPhoneValid
                                        ? "#55ce97"
                                        : "#e6e8ec",
                                    outline: "none",
                                    padding: "2px",
                                },
                                buttonStyle: {
                                    border: "none",
                                },
                            }}
                        />
                        {isPhoneValid && (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                style={{
                                    position: "absolute",
                                    top: "12px",
                                    right: "10px",
                                    fill: "#55ce97",
                                }}
                            >
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                            </svg>
                        )}
                    </div>
                </MDBCol>
                <MDBCol size={12} center>
                    <RangeDatePicker
                        excludeDates={[]}
                        discountedDates={[]}
                        promoDaysPresent={false}
                        rangeLength={rangeLength}
                        selectedDate={formValue.datesOfTravel.startDate}
                        lastDate={moment(generateLastDate()).toDate()}
                        setSelectedDate={onSelectDate}
                        click={click}
                        setClick={setClick}
                        startDateView={formValue.datesOfTravel.startDate}
                        onDeleteRange={() =>
                            setFormValue((prevState: IRequestAQuote) => ({
                                ...prevState,
                                datesOfTravel: {
                                    startDate: null,
                                    endDate: null,
                                },
                            }))
                        }
                        // eslint-disable-next-line @typescript-eslint/no-empty-function
                        setStartDateView={() => {}}
                        label={requestAQuoteFormMetadata?.datesOfTravelLabel}
                    />
                </MDBCol>
            </MDBRow>
            <MDBRow around>
                <MDBRow
                    style={{ alignItems: "end", margin: "10px 0", padding: 0 }}
                >
                    <MDBCol size={isSM ? 7 : 9}>
                        <Input
                            columnProps={{
                                size: 12,
                            }}
                            label={
                                requestAQuoteFormMetadata?.howManyPeopleLabel
                            }
                            required
                            type="select"
                            options={selectOptions()}
                            value={formValue.numberPeople}
                            onChangeSelect={onChange}
                            name="numberPeople"
                            validationCb={value =>
                                isFieldValid("numberPeople", value)
                            }
                            styleContainer={{
                                backgroundColor: "white",
                                borderRadius: "12px",
                            }}
                            isSelectConfigurator
                            inputClassName={styles.customSelect}
                        />
                    </MDBCol>
                    <MDBCol size={isSM ? 5 : 3}>
                        <Input
                            columnProps={{
                                size: 12,
                            }}
                            label={
                                requestAQuoteFormMetadata?.numberOfRoomsLabel
                            }
                            required
                            type="select"
                            options={selectOptions()}
                            value={formValue.numberRooms}
                            onChangeSelect={onChange}
                            name="numberRooms"
                            validationCb={value =>
                                isFieldValid(
                                    "numberRooms",
                                    value === "Other"
                                        ? formValue.numberRoomsComment
                                        : value,
                                    formValue.numberPeople === "Other"
                                        ? formValue.numberPeopleComment
                                        : formValue.numberPeople
                                )
                            }
                            styleContainer={{
                                backgroundColor: "white",
                                borderRadius: "12px",
                            }}
                            isSelectConfigurator
                            inputClassName={styles.customSelect}
                        />
                    </MDBCol>
                </MDBRow>
                <MDBRow style={{ padding: 0 }}>
                    <MDBCol size={isSM ? 7 : 9}>
                        {formValue.numberPeople === "Other" && (
                            <Input
                                placeholder="Write here..."
                                columnProps={{ size: 12, className: "my-2" }}
                                required={formValue.numberPeople === "Other"}
                                inputClassName={`${styles.customNumberInput}`}
                                type="number"
                                value={formValue?.numberPeopleComment}
                                onChange={onChange}
                                name="numberPeopleComment"
                                onDelete={() => onDelete("numberPeopleComment")}
                            />
                        )}
                    </MDBCol>
                    <MDBCol size={isSM ? 5 : 3}>
                        {formValue.numberRooms === "Other" && (
                            <Input
                                required={formValue.numberRooms === "Other"}
                                placeholder="Write here..."
                                columnProps={{ size: 12, className: "my-2" }}
                                inputClassName={`${styles.customNumberInput}`}
                                type="number"
                                value={formValue?.numberRoomsComment}
                                onChange={onChange}
                                validationCb={value => {
                                    return isFieldValid(
                                        "numberRooms",
                                        value,
                                        formValue.numberPeople === "Other"
                                            ? formValue.numberPeopleComment
                                            : formValue.numberPeople
                                    );
                                }}
                                name="numberRoomsComment"
                                onDelete={() => onDelete("numberRoomsComment")}
                            />
                        )}
                    </MDBCol>
                </MDBRow>
                <MDBCol size={12}>
                    <div
                        style={{ position: "relative", width: "100%" }}
                        className={styles.activitiesField}
                    >
                        <Input
                            columnProps={{
                                size: 12,
                                className: "my-2",
                            }}
                            label={
                                requestAQuoteFormMetadata?.activitiesToFocusLabel
                            }
                            required
                            type="multi-select"
                            options={formatRequestAQuoteActivities(activities)}
                            value={formValue.activitiesToFocus}
                            /* eslint-disable  @typescript-eslint/no-explicit-any */
                            onChangeSelect={(event: any) => {
                                const selectedValues = event.map(
                                    (item: { value: any }) => item.value
                                );
                                setFormValue((prevState: IRequestAQuote) => ({
                                    ...prevState,
                                    activitiesToFocus: selectedValues,
                                }));
                            }}
                            name="activitiesToFocus"
                        />
                    </div>
                </MDBCol>
                <MDBCol size={12} className={styles.destinationsField}>
                    <p className={`"w-100 ${styles.paragraph}`}>
                        {requestAQuoteFormMetadata?.preferredDestinationsLabel}*
                    </p>
                    <CitiesGoogle
                        validation={() =>
                            !!formValue.preferredDestinations.length
                        }
                        placeholder={
                            formValue.preferredDestinations.length
                                ? ""
                                : "Enter destination..."
                        }
                        setValue={city => {
                            if (city) {
                                setFormValue(prev => ({
                                    ...prev,
                                    preferredDestinations: [
                                        ...prev.preferredDestinations,
                                        city,
                                    ],
                                }));
                            }
                        }}
                        className={
                            formValue.preferredDestinations.length
                                ? styles.destinationsSelected
                                : styles.citiesGoogle
                        }
                    />
                    <MDBRow className="mt-2">
                        {formValue.preferredDestinations.map(destination => (
                            <Chip
                                value={destination}
                                onClick={() => {
                                    const updatedDestinations =
                                        formValue.preferredDestinations.filter(
                                            dest => dest !== destination
                                        );
                                    setFormValue(prev => ({
                                        ...prev,
                                        preferredDestinations:
                                            updatedDestinations,
                                    }));
                                }}
                            />
                        ))}
                    </MDBRow>
                </MDBCol>
                <MDBCol size={12}>
                    <MDBRow size={12}>
                        <Input
                            columnProps={{
                                size: isSM ? 12 : 6,
                                className: "my-2",
                            }}
                            label={requestAQuoteFormMetadata?.currencyLabel}
                            required
                            type="select"
                            options={[
                                {
                                    label: "Select...",
                                    value: "",
                                },
                                {
                                    label: "British Pound (GBP)",
                                    value: "British Pound (GBP)",
                                },
                                { label: "Euro (EUR)", value: "Euro (EUR)" },
                                {
                                    label: "US Dollar (USD)",
                                    value: "US Dollar (USD)",
                                },
                            ]}
                            value={formValue.currency}
                            onChangeSelect={onChange}
                            name="currency"
                            styleContainer={{
                                backgroundColor: "white",
                                borderRadius: "12px",
                            }}
                            isSelectConfigurator
                            inputClassName={styles.customSelect}
                        />
                        <Input
                            columnProps={{
                                size: isSM ? 12 : 6,
                                className: `my-2`,
                            }}
                            label={
                                requestAQuoteFormMetadata?.budgetPerPersonLabel
                            }
                            required
                            inputClassName={`${styles.customNumberInput}`}
                            type="number"
                            value={formValue.budgetPerPerson}
                            onChange={onChange}
                            name="budgetPerPerson"
                            onDelete={() => onDelete("budgetPerPerson")}
                            validationCb={val =>
                                isFieldValid("budgetPerPerson", val)
                            }
                        />
                    </MDBRow>
                </MDBCol>

                <MDBCol className="my-2">
                    <p className={`${styles.paragraph}`}>
                        {requestAQuoteFormMetadata?.specialNeedsLabel}
                    </p>
                    <MDBTextArea
                        name="note"
                        className="bg-white"
                        value={formValue.note}
                        id="note"
                        rows={2}
                        onChange={onChange}
                        maxLength={200}
                        placeholder="Write here..."
                        style={{
                            border: formValue.note
                                ? "2px solid #55ce97"
                                : "2px solid #e6e8ec",
                            borderRadius: "12px",
                            padding: "5px",
                        }}
                    />
                </MDBCol>

                <MDBRow className="d-flex gap-4 my-2" size={12}>
                    <MDBRow>
                        <MDBCol className="p-0">
                            <p className={`${styles.paragraphRadioButton}`}>
                                {
                                    requestAQuoteFormMetadata?.alreadyBookAFlightLabel
                                }
                            </p>
                            <MDBRadio
                                name="alreadyBookAFlight"
                                id="alreadyBookAFlightTrue"
                                value="YES"
                                label={
                                    <span className={styles.radioButtonLabel}>
                                        Yes
                                    </span>
                                }
                                checked={formValue.alreadyBookAFlight === "YES"}
                                onClick={() =>
                                    setFormValue(prev => ({
                                        ...prev,
                                        alreadyBookAFlight: "YES",
                                    }))
                                }
                                className={`PICustomCheckbox ${styles.radioButton}`}
                                inline
                            />
                            <MDBRadio
                                name="alreadyBookAFlight"
                                id="alreadyBookAFlightFalse"
                                label={
                                    <span className={styles.radioButtonLabel}>
                                        No
                                    </span>
                                }
                                checked={formValue.alreadyBookAFlight === "NO"}
                                onClick={() =>
                                    setFormValue(prev => ({
                                        ...prev,
                                        alreadyBookAFlight: "NO",
                                    }))
                                }
                                className={`PICustomCheckbox ${styles.radioButton}`}
                                inline
                            />
                        </MDBCol>
                    </MDBRow>
                    {formValue.alreadyBookAFlight === "NO" && (
                        <MDBRow size={12}>
                            <MDBCol className="p-0">
                                <p className={`${styles.paragraphRadioButton}`}>
                                    {
                                        requestAQuoteFormMetadata?.needAssistanceToBookAFlightLabel
                                    }
                                </p>
                                <MDBRadio
                                    name="needAssistance"
                                    id="needAssistanceTrue"
                                    label={
                                        <span
                                            className={styles.radioButtonLabel}
                                        >
                                            {" "}
                                            Yes{" "}
                                        </span>
                                    }
                                    checked={formValue.needAssistance === "YES"}
                                    onClick={() =>
                                        setFormValue(prev => ({
                                            ...prev,
                                            needAssistance: "YES",
                                        }))
                                    }
                                    className={`PICustomCheckbox ${styles.radioButton}`}
                                    inline
                                />
                                <MDBRadio
                                    name="needAssistance"
                                    id="needAssistanceFalse"
                                    label={
                                        <span
                                            className={styles.radioButtonLabel}
                                        >
                                            No
                                        </span>
                                    }
                                    checked={formValue.needAssistance === "NO"}
                                    onClick={() =>
                                        setFormValue(prev => ({
                                            ...prev,
                                            needAssistance: "NO",
                                        }))
                                    }
                                    className={`PICustomCheckbox ${styles.radioButton}`}
                                    inline
                                />
                            </MDBCol>
                        </MDBRow>
                    )}
                    <MDBRow size={12}>
                        <MDBCol className="p-0">
                            <p className={`${styles.paragraphRadioButton}`}>
                                {requestAQuoteFormMetadata?.needAssistanceLabel}
                            </p>
                            <MDBRadio
                                name="needTravelInsurance"
                                id="needTravelInsuranceTrue"
                                label={
                                    <span className={styles.radioButtonLabel}>
                                        {" "}
                                        Yes{" "}
                                    </span>
                                }
                                checked={
                                    formValue.needTravelInsurance === "YES"
                                }
                                onClick={() =>
                                    setFormValue(prev => ({
                                        ...prev,
                                        needTravelInsurance: "YES",
                                    }))
                                }
                                className={`PICustomCheckbox ${styles.radioButton}`}
                                inline
                            />
                            <MDBRadio
                                name="needTravelInsurance"
                                id="needTravelInsuranceFalse"
                                label={
                                    <span className={styles.radioButtonLabel}>
                                        No
                                    </span>
                                }
                                checked={formValue.needTravelInsurance === "NO"}
                                onClick={() =>
                                    setFormValue(prev => ({
                                        ...prev,
                                        needTravelInsurance: "NO",
                                    }))
                                }
                                className={`PICustomCheckbox ${styles.radioButton}`}
                                inline
                            />
                        </MDBCol>
                    </MDBRow>
                    {requestAQuoteFormMetadata?.questions.map(
                        (question, index) => (
                            <MDBCol className="p-0" size={12}>
                                <p className={styles.paragraphRadioButton}>
                                    {" "}
                                    {question}{" "}
                                </p>
                                <MDBTextArea
                                    value={
                                        formValue.answers[index]?.answer ?? ""
                                    }
                                    className="PICustomTextArea bg-white"
                                    rows={2}
                                    onChange={event =>
                                        onChangeAnswer(
                                            index,
                                            event.target.value
                                        )
                                    }
                                    maxLength={200}
                                    placeholder="Write here..."
                                />
                            </MDBCol>
                        )
                    )}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                        }}
                        className={`${styles.paragraphRadioButton}`}
                    >
                        <MDBCheckbox
                            id="privacyPolicyTrue"
                            checked={privacyPolicy}
                            onClick={() => setPrivacyPolicy(!privacyPolicy)}
                            className={`PICustomCheckbox ${styles.radioButton}`}
                        />
                        {requestAQuoteFormMetadata?.privacyConsentCaption && (
                            <p
                                className={`${styles.paragraphRadioButton}`}
                                dangerouslySetInnerHTML={{
                                    __html: convertDeltaToHtmlConverter(
                                        JSON.parse(
                                            requestAQuoteFormMetadata?.privacyConsentCaption ??
                                                "{'ops':[]}"
                                        )
                                    ),
                                }}
                            />
                        )}
                    </div>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                        }}
                    >
                        <MDBCheckbox
                            id="privacyPolicyMailChimpTrue"
                            checked={formValue.marketingConsent === "YES"}
                            // checked={privacyPolicyMailchimp}
                            onClick={() =>
                                setFormValue(prev => ({
                                    ...prev,
                                    marketingConsent:
                                        prev.marketingConsent === "YES"
                                            ? "NO"
                                            : "YES",
                                }))
                            }
                            className="PICustomCheckbox"
                        />
                        {requestAQuoteFormMetadata?.marketingConsentCaption && (
                            <p
                                className={`${styles.paragraphRadioButton}`}
                                dangerouslySetInnerHTML={{
                                    __html: convertDeltaToHtmlConverter(
                                        JSON.parse(
                                            requestAQuoteFormMetadata?.marketingConsentCaption ??
                                                "{'ops':[]}"
                                        )
                                    ),
                                }}
                            />
                        )}
                    </div>
                </MDBRow>
            </MDBRow>
            <MDBRow
                style={{
                    marginTop: "20px",
                    marginBottom: isInModal ? "20px" : "0px",
                }}
            >
                <MDBBtn
                    className={`w-100 ${
                        isFormValid()
                            ? styles.addToCartButton
                            : styles.addToCartButtonDisabled
                    }`}
                    onClick={() => {
                        if (isFormValid()) {
                            const item: GA4Item = {
                                item_id: id,
                                item_name: title,
                                item_brand: "Play italy",
                                item_category: "TOUR",
                                affiliation: "Play Italy Ecommerce",
                            };

                            const userData = {
                                email: formValue.email,
                                phone_number: formValue.cellphone,
                                first_name: formValue.firstName,
                            };

                            const activityNames = formatRequestAQuoteActivities(
                                activities
                            )
                                .filter(activity =>
                                    formValue.activitiesToFocus.includes(
                                        activity.value
                                    )
                                )
                                .map(activity => activity.label);

                            const formData = {
                                age: formValue.age,
                                people: formValue.numberPeople,
                                rooms: formValue.numberRooms,
                                activities: activityNames,
                                destinations: formValue.preferredDestinations,
                                currency: formValue.currency,
                                budget: formValue.budgetPerPerson,
                                flight: formValue.alreadyBookAFlight,
                                insurance: formValue.needTravelInsurance,
                                nationality: formValue.nationality,
                                transactionId: randomUid(),
                            };

                            const originalEventData = requestQuote(
                                item,
                                userData,
                                formData
                            );

                            TagManager.dataLayer(originalEventData);
                            const utm = getUTMParamsFromSession();
                            const formDataWithUTM = {
                                ...formValue,
                                utm,
                            };
                            requestAQuoteMutation(formDataWithUTM);
                        }
                    }}
                >
                    Request a quote
                </MDBBtn>
            </MDBRow>
        </MDBRow>
    );
};
